import { useState } from 'react';
import {
  IconSearch,
  IconFolder,
  IconFileText,
  IconPhoto,
  IconVideo,
  IconFileZip,
  IconDotsVertical,
  IconUsers,
} from '@tabler/icons-react';
import { ActionIcon, Avatar, Badge, Box, Card, Group, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { getSharedFiles, formatFileSize, FileItem } from '../mockData/files';

const getFileIcon = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return IconFolder;
    case 'image': return IconPhoto;
    case 'video': return IconVideo;
    case 'archive': return IconFileZip;
    default: return IconFileText;
  }
};

const getFileColor = (type: FileItem['type']) => {
  switch (type) {
    case 'folder': return 'blue';
    case 'image': return 'green';
    case 'video': return 'red';
    case 'pdf': return 'red';
    case 'document': return 'blue';
    case 'spreadsheet': return 'green';
    case 'presentation': return 'orange';
    case 'archive': return 'gray';
    case 'code': return 'violet';
    default: return 'gray';
  }
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

export default function SharedPage() {
  const [search, setSearch] = useState('');
  const sharedFiles = getSharedFiles();

  const filteredFiles = sharedFiles.filter(file =>
    file.name.toLowerCase().includes(search.toLowerCase()) ||
    file.ownerName.toLowerCase().includes(search.toLowerCase())
  );

  // Sort by modified date descending
  const sortedFiles = [...filteredFiles].sort((a, b) => b.modifiedAt.getTime() - a.modifiedAt.getTime());

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Shared with Me</Title>
          <Text c="dimmed" size="sm">{filteredFiles.length} shared files</Text>
        </Box>
      </Group>

      {/* Search */}
      <Card>
        <TextInput
          placeholder="Search shared files..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      {/* Shared Files List */}
      <Card>
        {sortedFiles.length === 0 ? (
          <Box ta="center" py="xl">
            <IconUsers size={48} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed" mt="md">No shared files</Text>
          </Box>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Owner</Table.Th>
                <Table.Th>Shared With</Table.Th>
                <Table.Th>Modified</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedFiles.map(file => {
                const Icon = getFileIcon(file.type);
                const color = getFileColor(file.type);
                return (
                  <Table.Tr key={file.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <Box
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            backgroundColor: `var(--mantine-color-${color}-light)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon size={16} color={`var(--mantine-color-${color}-filled)`} />
                        </Box>
                        <Box>
                          <Text size="sm">{file.name}</Text>
                          <Text size="xs" c="dimmed">{file.path}</Text>
                        </Box>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Avatar size="xs" radius="xl" color="blue">
                          {file.ownerName.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Text size="sm">{file.ownerName}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light">
                        {file.sharedWith?.length || 0} people
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{formatDate(file.modifiedAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{file.size ? formatFileSize(file.size) : '-'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" size="sm">
                        <IconDotsVertical size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
